/**
 * The procedural sound bank. Each entry is a pure recipe (sample rate -> Float32Array) so the
 * whole bank can be rendered offline, tested, and generated lazily at runtime.
 */
import { ahr, Biquad, drive, echo, env, finish, highpass, loopify, LP1, lowpass, mixInto, Noise, Osc, pluck, render, reverb, TAU } from './dsp';

export interface SoundDef {
  gen: (sr: number) => Float32Array;
  /** Normalized peak level (sets relative loudness across the bank). */
  peak: number;
  /** Seamless loop (beams, jetpack, heartbeat...). */
  loop?: boolean;
}

export const SOUNDS: Record<string, SoundDef> = {};
const add = (name: string, peak: number, gen: (sr: number) => Float32Array, loop = false): void => {
  SOUNDS[name] = { gen, peak, loop };
};

const midi = (m: number): number => 440 * Math.pow(2, (m - 69) / 12);
let seedCounter = 1;
const seed = (): number => (seedCounter++ * 0x9e3779b1) >>> 0;

// Building blocks ---------------------------------------------------------------------------

/** Band-passed noise burst (transients, cracks, clicks). */
function crackLayer(sr: number, dur: number, freq: number, q: number, decay: number, s: number, attack = 0.0004): Float32Array {
  const n = new Noise(s);
  const bp = new Biquad(sr, 'bp');
  bp.set(freq, q);
  return render(dur, sr, (t) => bp.run(n.next()) * env(t, attack, decay) * (1 + q));
}

/** Sine with an exponential pitch sweep (thumps, kicks, booms). */
function sweepLayer(sr: number, dur: number, f0: number, f1: number, sweep: number, decay: number, attack = 0.001): Float32Array {
  const o = new Osc(sr);
  return render(dur, sr, (t) => o.sine(f1 + (f0 - f1) * Math.exp(-t / sweep)) * env(t, attack, decay));
}

/** Low-passed noise whose cutoff falls over time (gun bodies, whooshes, rumbles). */
function bodyLayer(sr: number, dur: number, cut0: number, cut1: number, fall: number, decay: number, s: number, attack = 0.001): Float32Array {
  const n = new Noise(s);
  const lp = new LP1(sr);
  const lp2 = new LP1(sr);
  return render(dur, sr, (t) => {
    const c = cut1 + (cut0 - cut1) * Math.exp(-t / fall);
    return lp2.run(lp.run(n.next(), c), c) * env(t, attack, decay) * 2.2;
  });
}

/** Inharmonic metallic ring (actions, clanks, bells, pans). */
function ringLayer(sr: number, dur: number, freqs: number[], decay: number, delay = 0, amps?: number[]): Float32Array {
  const out = new Float32Array(Math.max(1, Math.floor(dur * sr)));
  const start = Math.floor(delay * sr);
  for (let k = 0; k < freqs.length; k++) {
    // Rotating phasor + per-sample decay multiplier: no trig/exp in the inner loop.
    const w = (TAU * freqs[k]) / sr;
    const cw = Math.cos(w);
    const sw = Math.sin(w);
    const mul = Math.exp(-(1 + k * 0.35) / (decay * sr));
    let re = 1;
    let im = 0;
    let amp = amps?.[k] ?? 1 / (k + 1);
    const attack = Math.max(1, Math.floor(0.0008 * sr));
    for (let i = start, j = 0; i < out.length; i++, j++) {
      out[i] += im * amp * (j < attack ? j / attack : 1);
      const nr = re * cw - im * sw;
      im = re * sw + im * cw;
      re = nr;
      amp *= mul;
      if (amp < 1e-5) break;
    }
  }
  return out;
}

/** Scattered crackles (debris, fireworks, grit). density: clicks per second at t = 0. */
function crackles(sr: number, dur: number, density: number, freq: number, s: number, fade = dur): Float32Array {
  const n = new Noise(s);
  const out = new Float32Array(Math.floor(dur * sr));
  const bp = new Biquad(sr, 'bp');
  bp.set(freq, 1.2);
  let t = 0;
  while (t < dur) {
    const rate = density * Math.exp(-t / fade);
    t += (-Math.log(1 - n.unit() * 0.999) / Math.max(1, rate)) || 0.01;
    const at = Math.floor(t * sr);
    const len = Math.floor((0.002 + n.unit() * 0.006) * sr);
    const amp = (0.4 + n.unit() * 0.6) * Math.exp(-t / fade);
    for (let i = 0; i < len && at + i < out.length; i++) out[at + i] += n.next() * amp * (1 - i / len);
  }
  for (let i = 0; i < out.length; i++) out[i] = bp.run(out[i]) * 2.5;
  return out;
}

/** Simple synth notes for jingles: [midi, start, dur, vol]. */
function tones(sr: number, list: [number, number, number, number?][], timbre: 'square' | 'pulse' | 'tri' | 'bell' | 'sine' | 'saw', tail = 0.35): Float32Array {
  let end = 0;
  for (const [, st, du] of list) end = Math.max(end, st + du);
  const out = new Float32Array(Math.floor((end + tail) * sr));
  for (const [m, st, du, vol = 1] of list) {
    const f = midi(m);
    const o = new Osc(sr);
    const o2 = new Osc(sr);
    const o3 = new Osc(sr);
    const note = render(du + tail, sr, (t) => {
      let v: number;
      switch (timbre) {
        case 'square':
          v = o.square(f, 0.5) * 0.5;
          break;
        case 'pulse':
          v = o.square(f, 0.25) * 0.5;
          break;
        case 'tri':
          v = o.tri(f);
          break;
        case 'saw':
          v = o.saw(f) * 0.6;
          break;
        case 'bell':
          v = o.sine(f) + o2.sine(f * 2.76) * 0.35 * Math.exp(-t / 0.15) + o3.sine(f * 5.4) * 0.18 * Math.exp(-t / 0.07);
          break;
        default:
          v = o.sine(f);
      }
      const e = timbre === 'bell' ? env(t, 0.002, du * 0.9 + 0.12) : ahr(t, 0.004, Math.max(0, du - 0.03), tail * 0.6) * (0.75 + 0.25 * Math.exp(-t / 0.06));
      return v * e * vol;
    });
    mixInto(out, note, st, sr);
  }
  return out;
}

const sum = (sr: number, dur: number, layers: [Float32Array, number, number?][]): Float32Array => {
  const out = new Float32Array(Math.floor(dur * sr));
  for (const [buf, gain, offset = 0] of layers) mixInto(out, buf, offset, sr, gain);
  return out;
};

// Guns --------------------------------------------------------------------------------------

interface Gun {
  dur: number;
  /** Transient: [center Hz, Q, level, decay s]. */
  crack: [number, number, number, number];
  /** Low punch: [start Hz, end Hz, sweep s, level, decay s]. */
  thump: [number, number, number, number, number];
  /** Noise body: [cutoff start, cutoff end, level, decay s]. */
  body: [number, number, number, number];
  /** Mechanical action ring: [delay s, level, partials Hz]. */
  mech?: [number, number, number[]];
  /** Tonal sweep for energy weapons: [start Hz, end Hz, level, decay s, wave]. */
  zip?: [number, number, number, number, 'sine' | 'square' | 'saw'];
  /** Room tail: [level, decay s, cutoff Hz]. */
  tail: [number, number, number];
  echo: [number, number][];
  drive: number;
  /** Extra pellet/grit spray level (shotguns). */
  spray?: number;
}

function gunSound(g: Gun, far: boolean): (sr: number) => Float32Array {
  const s = seed();
  return (sr) => {
    const dur = far ? g.dur * 1.35 + 0.3 : g.dur;
    const [cf, cq, cl, cd] = g.crack;
    const [tf0, tf1, ts, tl, td] = g.thump;
    const [b0, b1, bl, bd] = g.body;
    const layers: [Float32Array, number, number?][] = [];
    if (!far) layers.push([crackLayer(sr, 0.08, cf, cq, cd, s), cl]);
    layers.push([sweepLayer(sr, Math.min(dur, 0.6), tf0, tf1, ts, td * (far ? 1.6 : 1)), tl * (far ? 0.9 : 1)]);
    layers.push([bodyLayer(sr, dur, far ? b0 * 0.3 : b0, far ? b1 * 0.5 : b1, 0.03, bd * (far ? 1.8 : 1), s + 1), bl]);
    if (g.mech && !far) layers.push([ringLayer(sr, 0.12, g.mech[2], 0.018, g.mech[0]), g.mech[1]]);
    if (g.zip) {
      const [z0, z1, zl, zd, wave] = g.zip;
      const o = new Osc(sr);
      const zb = render(Math.min(dur, zd * 6), sr, (t) => {
        const f = z1 + (z0 - z1) * Math.exp(-t / (zd * 0.8));
        const v = wave === 'sine' ? o.sine(f) : wave === 'square' ? o.square(f, 0.3) * 0.6 : o.saw(f) * 0.6;
        return v * env(t, 0.001, zd);
      });
      layers.push([far ? lowpass(zb, sr, 1500) : zb, zl]);
    }
    if (g.spray && !far) layers.push([crackles(sr, 0.12, 900, 3500, s + 2, 0.05), g.spray]);
    const [tl2, tdec, tcut] = g.tail;
    layers.push([bodyLayer(sr, dur, tcut, tcut * 0.6, 0.2, tdec * (far ? 1.5 : 1), s + 3, 0.012), tl2 * (far ? 1.8 : 1)]);
    const out = sum(sr, dur, layers);
    drive(out, g.drive);
    echo(out, sr, far ? g.echo.map(([d, a]) => [d * 1.4, Math.min(0.6, a * 1.6)] as [number, number]) : g.echo, far ? 0.6 : 0.35);
    if (far) lowpass(out, sr, 1300);
    return out;
  };
}

const GUNS: Record<string, Gun> = {
  rifle: { dur: 0.55, crack: [2600, 0.9, 0.9, 0.006], thump: [165, 55, 0.05, 1.0, 0.045], body: [4200, 700, 1.1, 0.055], mech: [0.035, 0.16, [2300, 3650, 5200]], tail: [0.28, 0.22, 900], echo: [[0.09, 0.25], [0.19, 0.12]], drive: 1.8 },
  rifle_heavy: { dur: 0.85, crack: [2200, 0.8, 1.0, 0.008], thump: [130, 45, 0.07, 1.25, 0.07], body: [3600, 500, 1.2, 0.08], mech: [0.05, 0.14, [1900, 3100, 4400]], tail: [0.35, 0.35, 800], echo: [[0.11, 0.3], [0.24, 0.16]], drive: 2.0 },
  smg: { dur: 0.35, crack: [3300, 1.0, 0.8, 0.004], thump: [215, 80, 0.03, 0.7, 0.03], body: [5200, 1200, 0.9, 0.035], mech: [0.02, 0.12, [2800, 4200]], tail: [0.18, 0.12, 1100], echo: [[0.07, 0.2]], drive: 1.6 },
  lmg: { dur: 0.5, crack: [2400, 0.8, 0.9, 0.006], thump: [140, 48, 0.06, 1.2, 0.06], body: [3600, 600, 1.1, 0.06], mech: [0.03, 0.18, [1700, 2900]], tail: [0.28, 0.2, 800], echo: [[0.09, 0.25]], drive: 2.2 },
  minigun: { dur: 0.16, crack: [3000, 1.1, 0.7, 0.003], thump: [180, 90, 0.02, 0.85, 0.02], body: [4500, 1500, 0.8, 0.025], tail: [0.08, 0.05, 1500], echo: [], drive: 2.4 },
  pistol: { dur: 0.42, crack: [3000, 1.0, 0.9, 0.005], thump: [205, 70, 0.04, 0.9, 0.035], body: [4600, 900, 1.0, 0.042], mech: [0.025, 0.2, [3100, 4700]], tail: [0.2, 0.16, 1000], echo: [[0.08, 0.22]], drive: 1.6 },
  revolver: { dur: 0.75, crack: [2300, 0.8, 1.0, 0.007], thump: [120, 42, 0.07, 1.35, 0.075], body: [3600, 450, 1.3, 0.075], tail: [0.35, 0.3, 700], echo: [[0.12, 0.3], [0.26, 0.15]], drive: 2.2 },
  shotgun: { dur: 0.85, crack: [1800, 0.6, 1.0, 0.01], thump: [110, 40, 0.08, 1.45, 0.08], body: [3000, 400, 1.5, 0.1], tail: [0.4, 0.3, 700], echo: [[0.1, 0.3], [0.22, 0.18]], drive: 2.6, spray: 0.35 },
  shotgun_heavy: { dur: 1.05, crack: [1600, 0.6, 1.1, 0.012], thump: [95, 34, 0.1, 1.6, 0.1], body: [2800, 350, 1.6, 0.13], tail: [0.5, 0.4, 600], echo: [[0.12, 0.35], [0.27, 0.2]], drive: 3, spray: 0.4 },
  sniper: { dur: 1.45, crack: [2800, 0.9, 1.1, 0.006], thump: [100, 38, 0.09, 1.5, 0.09], body: [3200, 400, 1.3, 0.1], tail: [0.5, 0.6, 700], echo: [[0.18, 0.35], [0.4, 0.22], [0.7, 0.12]], drive: 2.4 },
  sniper_heavy: { dur: 1.75, crack: [2400, 0.8, 1.2, 0.008], thump: [80, 30, 0.12, 1.8, 0.12], body: [3000, 300, 1.5, 0.13], mech: [0, 0.22, [900, 1400, 2300]], tail: [0.55, 0.7, 600], echo: [[0.2, 0.4], [0.45, 0.25], [0.8, 0.14]], drive: 3 },
  pulse: { dur: 0.3, crack: [4200, 1.4, 0.35, 0.003], thump: [240, 90, 0.03, 0.6, 0.03], body: [3000, 900, 0.35, 0.03], zip: [1500, 330, 1.0, 0.07, 'square'], tail: [0.12, 0.1, 1400], echo: [[0.07, 0.18]], drive: 1.4 },
  laser: { dur: 0.32, crack: [5200, 1.6, 0.3, 0.002], thump: [300, 120, 0.02, 0.4, 0.02], body: [3000, 1500, 0.2, 0.02], zip: [2300, 520, 1.0, 0.09, 'sine'], tail: [0.1, 0.12, 1800], echo: [[0.08, 0.2]], drive: 1.2 },
};

/** Per-weapon tweaks so weapons sharing a sound key still sound different. */
const VARIANTS: Record<string, [string, (g: Gun) => void]> = {
  brick_rifle: ['rifle', () => {}],
  tempest_ar: ['rifle', (g) => {
    g.crack[0] = 3100;
    g.thump[0] = 185;
    g.body[3] *= 0.8;
    g.tail[1] *= 0.8;
  }],
  buzz_smg: ['smg', () => {}],
  hornet_smg: ['smg', (g) => {
    g.crack[0] = 4200;
    g.thump = [260, 110, 0.02, 0.55, 0.022];
    g.body[3] *= 0.75;
    g.dur = 0.28;
  }],
  wasp_pdw: ['smg', (g) => {
    g.thump[0] = 190;
    g.zip = [2600, 900, 0.35, 0.03, 'square'];
  }],
  scattergun: ['shotgun', () => {}],
  twin_barrel: ['shotgun_heavy', () => {}],
  auto_scatter: ['shotgun', (g) => {
    g.thump[0] = 125;
    g.body[3] *= 0.75;
    g.tail[1] *= 0.7;
    g.dur = 0.65;
  }],
  sawed_off: ['shotgun', (g) => {
    g.crack[2] = 1.2;
    g.thump = [100, 36, 0.08, 1.6, 0.09];
    g.tail[1] *= 0.7;
  }],
  pulse_carbine: ['pulse', () => {}],
  ranger_br: ['rifle_heavy', () => {}],
  hunter_dmr: ['rifle_heavy', (g) => {
    g.crack[0] = 3000;
    g.tail[0] *= 1.25;
    g.echo = [[0.14, 0.32], [0.33, 0.18]];
  }],
  thumper_lmg: ['lmg', () => {}],
  voxel_shredder: ['minigun', () => {}],
  peashooter: ['pistol', () => {}],
  machine_pistol: ['pistol', (g) => {
    g.crack[0] = 3700;
    g.thump = [240, 90, 0.03, 0.7, 0.025];
    g.body[3] *= 0.75;
    g.dur = 0.3;
  }],
  hand_cannon: ['revolver', () => {}],
  photon_pistol: ['laser', () => {}],
  longshot: ['sniper', () => {}],
  pierce_rifle: ['sniper_heavy', () => {}],
};

/** Loudness by caliber: small arms sit under rifles, big guns hit hardest. */
const GUN_PEAK: Record<string, number> = {
  smg: 0.78,
  minigun: 0.72,
  pistol: 0.8,
  laser: 0.74,
  pulse: 0.78,
  rifle: 0.88,
  lmg: 0.9,
  rifle_heavy: 0.93,
  revolver: 0.95,
  shotgun: 0.96,
  shotgun_heavy: 0.98,
  sniper: 1.0,
  sniper_heavy: 1.0,
};
for (const [name, g] of Object.entries(GUNS)) {
  add(name, GUN_PEAK[name] ?? 0.9, gunSound(g, false));
  add(`${name}_far`, (GUN_PEAK[name] ?? 0.9) * 0.65, gunSound(g, true));
}
for (const [id, [base, tweak]] of Object.entries(VARIANTS)) {
  const g: Gun = JSON.parse(JSON.stringify(GUNS[base]));
  tweak(g);
  add(`w_${id}`, GUN_PEAK[base] ?? 0.9, gunSound(g, false));
  add(`w_${id}_far`, (GUN_PEAK[base] ?? 0.9) * 0.65, gunSound(g, true));
}

// Launchers, specials -------------------------------------------------------------------------

function nailGen(sr: number): Float32Array {
  const s = seed();
  const hiss = highpass(bodyLayer(sr, 0.12, 9000, 6000, 0.05, 0.035, s), sr, 2500);
  return sum(sr, 0.32, [
    [hiss, 1.1],
    [ringLayer(sr, 0.1, [2100, 3350, 5100], 0.014, 0.012), 0.5],
    [sweepLayer(sr, 0.12, 190, 90, 0.02, 0.025), 0.6],
    [crackLayer(sr, 0.05, 2600, 2, 0.004, s + 1), 0.5],
  ]);
}
add('nail', 0.85, nailGen);
add('w_nail_driver', 0.85, nailGen);
add('nail_far', 0.45, (sr) => lowpass(nailGen(sr), sr, 1200));

function crossbowGen(sr: number): Float32Array {
  const n = new Noise(seed());
  const twang = pluck(150, 0.6, sr, n, 0.3, 0.9985);
  const knock = crackLayer(sr, 0.08, 420, 4, 0.02, seed());
  const slap = crackLayer(sr, 0.05, 1800, 0.8, 0.006, seed());
  return sum(sr, 0.65, [[twang, 0.55], [knock, 0.8], [slap, 0.5], [sweepLayer(sr, 0.1, 160, 80, 0.02, 0.03), 0.4]]);
}
add('crossbow', 0.8, crossbowGen);
add('crossbow_far', 0.4, (sr) => lowpass(crossbowGen(sr), sr, 1000));

add('railgun', 0.95, (sr) => {
  const s = seed();
  const o = new Osc(sr);
  const zing = render(0.5, sr, (t) => o.sine(400 + 5600 * Math.exp(-t / 0.07)) * env(t, 0.001, 0.12));
  const out = sum(sr, 1.4, [
    [crackLayer(sr, 0.08, 3200, 0.9, 0.006, s), 1.0],
    [zing, 0.7],
    [ringLayer(sr, 1.2, [1100, 2650, 4300, 5900], 0.45, 0, [0.5, 0.4, 0.3, 0.2]), 0.5],
    [sweepLayer(sr, 0.5, 90, 30, 0.08, 0.12), 1.3],
    [bodyLayer(sr, 1.2, 2500, 400, 0.05, 0.3, s + 1), 0.6],
  ]);
  drive(out, 1.8);
  return echo(out, sr, [[0.2, 0.3], [0.45, 0.15]]);
});
add('railgun_far', 0.55, (sr) => lowpass(SOUNDS.railgun.gen(sr), sr, 1400));

add('plasma', 0.9, (sr) => {
  const s = seed();
  const car = new Osc(sr);
  const mod = new Osc(sr);
  const whomp = render(0.7, sr, (t) => {
    const f = 90 + 170 * Math.exp(-t / 0.08);
    const idx = 4 * Math.exp(-t / 0.12);
    return Math.sin(TAU * car.step(f) + idx * Math.sin(TAU * mod.step(f * 0.5))) * env(t, 0.004, 0.18);
  });
  const sizzle = highpass(bodyLayer(sr, 0.6, 7000, 3000, 0.1, 0.12, s), sr, 2000);
  return sum(sr, 0.75, [[whomp, 1.0], [sizzle, 0.45], [sweepLayer(sr, 0.4, 70, 35, 0.06, 0.12), 0.8]]);
});
add('plasma_far', 0.5, (sr) => lowpass(SOUNDS.plasma.gen(sr), sr, 1000));

add('rocket', 0.95, (sr) => {
  const s = seed();
  const n = new Noise(s);
  const bp = new Biquad(sr, 'bp');
  const whoosh = render(1.2, sr, (t) => {
    const f = t < 0.08 ? 400 + (1800 - 400) * (t / 0.08) : 900 + 900 * Math.exp(-(t - 0.08) / 0.2);
    return bp.run(n.next(), f, 0.9) * env(t, 0.012, 0.45) * 2.4;
  });
  const hiss = highpass(bodyLayer(sr, 1.2, 8000, 5000, 0.3, 0.6, s + 1, 0.05), sr, 3000);
  const out = sum(sr, 1.3, [[sweepLayer(sr, 0.5, 95, 38, 0.06, 0.12), 1.2], [whoosh, 1.0], [hiss, 0.35], [crackLayer(sr, 0.06, 1500, 0.7, 0.01, s + 2), 0.6]]);
  drive(out, 1.6);
  return echo(out, sr, [[0.12, 0.25]]);
});
add('rocket_far', 0.55, (sr) => lowpass(SOUNDS.rocket.gen(sr), sr, 900));

add('grenade_launch', 0.9, (sr) => {
  const s = seed();
  const bp = new Biquad(sr, 'bp');
  bp.set(300, 6);
  const n = new Noise(s);
  const hollow = render(0.3, sr, (t) => bp.run(n.next()) * env(t, 0.001, 0.05) * 6);
  return sum(sr, 0.45, [[sweepLayer(sr, 0.3, 150, 60, 0.03, 0.06), 1.3], [crackLayer(sr, 0.05, 900, 1.2, 0.008, s + 1), 0.7], [hollow, 0.6]]);
});
add('grenade_launch_far', 0.45, (sr) => lowpass(SOUNDS.grenade_launch.gen(sr), sr, 800));
add('w_bouncer', 0.9, SOUNDS.grenade_launch.gen);

add('mortar', 0.95, (sr) => {
  const s = seed();
  const out = sum(sr, 1.2, [
    [sweepLayer(sr, 0.8, 75, 32, 0.08, 0.2), 1.5],
    [bodyLayer(sr, 1.0, 1200, 200, 0.05, 0.25, s), 1.1],
    [crackLayer(sr, 0.06, 700, 0.7, 0.012, s + 1), 0.6],
  ]);
  drive(out, 2);
  return echo(out, sr, [[0.16, 0.35], [0.36, 0.2]]);
});
add('mortar_far', 0.55, (sr) => lowpass(SOUNDS.mortar.gen(sr), sr, 700));

add('flare', 0.8, (sr) => {
  const s = seed();
  const n = new Noise(s);
  const hp = new Biquad(sr, 'hp');
  hp.set(3500, 0.7);
  const fizz = render(0.8, sr, (t) => hp.run(n.next()) * env(t, 0.02, 0.35) * (0.6 + 0.4 * Math.sin(t * 90 + Math.sin(t * 37) * 3)));
  return sum(sr, 0.85, [[sweepLayer(sr, 0.2, 210, 100, 0.02, 0.04), 1.0], [crackLayer(sr, 0.05, 1600, 1, 0.006, s + 1), 0.8], [fizz, 0.6]]);
});
add('flare_far', 0.4, (sr) => lowpass(SOUNDS.flare.gen(sr), sr, 1100));

add('build', 0.75, (sr) => {
  const s = seed();
  return sum(sr, 0.25, [[sweepLayer(sr, 0.15, 260, 150, 0.02, 0.04), 0.9], [crackLayer(sr, 0.1, 900, 2, 0.02, s), 0.8], [bodyLayer(sr, 0.2, 3000, 800, 0.03, 0.05, s + 1), 0.3]]);
});
add('w_block_gun', 0.75, SOUNDS.build.gen);

// Melee -------------------------------------------------------------------------------------

function whoosh(sr: number, dur: number, f0: number, f1: number, f2: number, level: number, s: number): Float32Array {
  const n = new Noise(s);
  const bp = new Biquad(sr, 'bp');
  return render(dur, sr, (t) => {
    const k = t / dur;
    const f = k < 0.5 ? f0 + (f1 - f0) * (k * 2) : f1 + (f2 - f1) * ((k - 0.5) * 2);
    return bp.run(n.next(), f, 1.4) * Math.sin(Math.PI * Math.min(1, k * 1.05)) * level * 3;
  });
}
add('swing', 0.7, (sr) => whoosh(sr, 0.26, 350, 1400, 600, 1, seed()));
add('swing_heavy', 0.75, (sr) => whoosh(sr, 0.32, 220, 800, 350, 1, seed()));
add('swing_blade', 0.7, (sr) => {
  const s = seed();
  const o = new Osc(sr);
  const whistle = render(0.25, sr, (t) => o.sine(1800 + 1400 * Math.sin((t / 0.25) * Math.PI)) * Math.sin((t / 0.25) * Math.PI) * 0.25);
  return sum(sr, 0.27, [[whoosh(sr, 0.25, 600, 2600, 900, 1, s), 1], [whistle, 0.5]]);
});
add('swing_energy', 0.7, (sr) => {
  const o = new Osc(sr);
  const o2 = new Osc(sr);
  const hum = render(0.3, sr, (t) => (o.saw(110 + 70 * Math.sin((t / 0.3) * Math.PI)) * 0.5 + o2.sine(220 + 180 * Math.sin((t / 0.3) * Math.PI))) * Math.sin((t / 0.3) * Math.PI));
  return sum(sr, 0.32, [[lowpass(hum, sr, 1800), 0.9], [whoosh(sr, 0.3, 500, 1800, 700, 1, seed()), 0.5]]);
});
add('hammer', 0.9, (sr) => sum(sr, 0.45, [[sweepLayer(sr, 0.2, 140, 60, 0.02, 0.05), 1.2], [crackLayer(sr, 0.1, 1200, 0.8, 0.02, seed()), 0.8], [ringLayer(sr, 0.4, [620, 1470, 2350], 0.12, 0.005), 0.35]]));
add('w_builders_hammer', 0.9, SOUNDS.hammer.gen);
add('w_pickaxe', 0.85, (sr) => sum(sr, 0.4, [[ringLayer(sr, 0.35, [1650, 2900, 4300], 0.08, 0), 0.5], [crackLayer(sr, 0.1, 2200, 1, 0.015, seed()), 0.8], [sweepLayer(sr, 0.12, 180, 90, 0.02, 0.03), 0.6]]));
add('pan', 0.9, (sr) => {
  const out = sum(sr, 1.1, [[ringLayer(sr, 1.1, [520, 1135, 1850, 2610, 3480], 0.55, 0, [1, 0.7, 0.5, 0.35, 0.2]), 0.9], [sweepLayer(sr, 0.15, 160, 80, 0.02, 0.04), 0.7], [crackLayer(sr, 0.05, 1500, 1, 0.006, seed()), 0.5]]);
  return drive(out, 1.3);
});
add('w_frying_pan', 0.9, SOUNDS.pan.gen);
add('blade', 0.85, (sr) => sum(sr, 0.5, [[ringLayer(sr, 0.45, [2900, 4100, 6200], 0.16, 0.01), 0.45], [whoosh(sr, 0.18, 900, 3500, 1800, 1, seed()), 0.7], [crackLayer(sr, 0.05, 3000, 1.2, 0.006, seed()), 0.5]]));
add('w_katana', 0.85, SOUNDS.blade.gen);
add('w_plasma_blade', 0.85, (sr) => {
  const o = new Osc(sr);
  const zap = render(0.45, sr, (t) => o.saw(140 + 90 * Math.exp(-t / 0.05)) * env(t, 0.002, 0.12));
  return sum(sr, 0.5, [[lowpass(zap, sr, 2400), 0.8], [crackLayer(sr, 0.1, 4200, 1.5, 0.02, seed()), 0.5], [ringLayer(sr, 0.3, [880, 1320], 0.1, 0), 0.3]]);
});
add('claw', 0.8, (sr) => sum(sr, 0.3, [[whoosh(sr, 0.18, 1200, 3200, 1500, 1, seed()), 0.8], [crackles(sr, 0.2, 300, 2500, seed(), 0.08), 0.5]]));
add('w_zombie_claws', 0.8, SOUNDS.claw.gen);
add('saw', 0.8, (sr) => {
  const o = new Osc(sr);
  const m = new Osc(sr);
  const s = seed();
  const buzz = render(0.3, sr, (t) => o.saw(170 + 30 * m.sine(31)) * env(t, 0.004, 0.12));
  return sum(sr, 0.32, [[lowpass(buzz, sr, 2800), 0.9], [crackles(sr, 0.25, 1500, 3000, s, 0.1), 0.35]]);
});
add('w_buzz_blade', 0.8, SOUNDS.saw.gen);

// Explosions ----------------------------------------------------------------------------------

function boomGen(size: number, far: boolean, sci = false): (sr: number) => Float32Array {
  const s = seed();
  return (sr) => {
    const dur = 1.2 + size * 1.4 + (far ? 0.6 : 0);
    const layers: [Float32Array, number, number?][] = [
      [sweepLayer(sr, Math.min(dur, 1.6), 60 + 20 / size, 24, 0.12 * size, 0.35 * size + 0.1, 0.004), 1.6],
      [bodyLayer(sr, dur, far ? 700 : 3200, 160, 0.12 * size, 0.35 * size + 0.12, s, 0.003), far ? 1.1 : 1.3],
      [bodyLayer(sr, dur, 260, 90, 0.4, 0.7 * size + 0.2, s + 1, 0.05), 0.9],
    ];
    if (!far) {
      layers.push([crackLayer(sr, 0.05, 1800, 0.5, 0.012, s + 2), 1.1]);
      layers.push([crackles(sr, dur, 110 * size + 30, 2600, s + 3, 0.35 * size + 0.2), 0.45, 0.08]);
    }
    if (sci) {
      const car = new Osc(sr);
      const mod = new Osc(sr);
      layers.push([render(0.7, sr, (t) => Math.sin(TAU * car.step(160 + 500 * Math.exp(-t / 0.1)) + 3 * Math.sin(TAU * mod.step(77))) * env(t, 0.002, 0.2)), 0.6]);
    }
    const out = sum(sr, dur, layers);
    drive(out, far ? 1.5 : 2.4);
    echo(out, sr, far ? [[0.25, 0.4], [0.6, 0.25]] : [[0.15, 0.3], [0.35, 0.18]], 0.5);
    if (far) lowpass(out, sr, 600);
    return out;
  };
}
add('explosion', 1.0, boomGen(1, false));
add('explosion_far', 0.7, boomGen(1, true));
add('explosion_small', 0.9, boomGen(0.6, false));
add('explosion_small_far', 0.55, boomGen(0.6, true));
add('explosion_plasma', 0.95, boomGen(0.8, false, true));
add('collapse', 0.95, (sr) => {
  const s = seed();
  const out = sum(sr, 2.8, [
    [bodyLayer(sr, 2.8, 400, 100, 0.8, 1.0, s, 0.2), 1.2],
    [crackles(sr, 2.6, 160, 1400, s + 1, 1.2), 0.6, 0.1],
    [crackles(sr, 2.6, 60, 400, s + 2, 1.4), 0.8, 0.15],
    [sweepLayer(sr, 1.6, 55, 28, 0.4, 0.6, 0.08), 0.9],
  ]);
  return echo(drive(out, 1.6), sr, [[0.2, 0.25]]);
});

// Voxel destruction by material ---------------------------------------------------------------

function breakGen(kind: 'stone' | 'brick' | 'concrete' | 'wood' | 'glass' | 'ice' | 'crystal' | 'metal' | 'soft' | 'leaves' | 'plastic' | 'snow' | 'cloth'): (sr: number) => Float32Array {
  const s = seed();
  return (sr) => {
    switch (kind) {
      case 'stone':
        return sum(sr, 0.35, [[crackles(sr, 0.3, 900, 1800, s, 0.09), 0.9], [sweepLayer(sr, 0.12, 150, 70, 0.02, 0.04), 0.6], [bodyLayer(sr, 0.3, 2500, 500, 0.04, 0.07, s + 1), 0.5]]);
      case 'brick':
        return sum(sr, 0.35, [[crackles(sr, 0.3, 700, 1300, s, 0.1), 0.9], [crackLayer(sr, 0.1, 700, 1.5, 0.02, s + 1), 0.7], [sweepLayer(sr, 0.12, 130, 60, 0.02, 0.05), 0.6]]);
      case 'concrete':
        return sum(sr, 0.4, [[crackles(sr, 0.35, 600, 1100, s, 0.12), 0.8], [sweepLayer(sr, 0.18, 110, 45, 0.03, 0.07), 0.9], [bodyLayer(sr, 0.35, 1800, 300, 0.05, 0.1, s + 1), 0.6]]);
      case 'wood': {
        const res = new Biquad(sr, 'bp');
        res.set(620, 5);
        const n = new Noise(s);
        const knock = render(0.25, sr, (t) => res.run(n.next()) * env(t, 0.0005, 0.04) * 6);
        return sum(sr, 0.3, [[crackLayer(sr, 0.05, 2400, 1, 0.004, s + 1), 0.8], [knock, 0.8], [crackles(sr, 0.25, 500, 2000, s + 2, 0.07), 0.5]]);
      }
      case 'glass':
      case 'ice':
      case 'crystal': {
        const out = new Float32Array(Math.floor(0.55 * sr));
        const n = new Noise(s);
        const count = kind === 'glass' ? 16 : kind === 'ice' ? 10 : 12;
        for (let k = 0; k < count; k++) {
          const f = kind === 'glass' ? 3000 + n.unit() * 4500 : kind === 'ice' ? 1800 + n.unit() * 2500 : 1200 + Math.floor(n.unit() * 5) * 520;
          mixInto(out, ringLayer(sr, 0.25, [f, f * 1.48], kind === 'crystal' ? 0.12 : 0.05, 0), 0.02 + n.unit() * 0.28 * (k / count), sr, 0.25 + n.unit() * 0.3);
        }
        mixInto(out, crackLayer(sr, 0.12, kind === 'ice' ? 2200 : 4200, 0.8, 0.03, s + 1), 0, sr, 0.9);
        return out;
      }
      case 'metal':
        return sum(sr, 0.5, [[ringLayer(sr, 0.45, [830, 1310, 2110, 2950], 0.14, 0, [0.8, 0.6, 0.45, 0.3]), 0.7], [crackLayer(sr, 0.08, 1900, 1, 0.01, s), 0.8], [sweepLayer(sr, 0.1, 200, 90, 0.02, 0.03), 0.4]]);
      case 'plastic': {
        const bp = new Biquad(sr, 'bp');
        bp.set(1250, 8);
        const n = new Noise(s);
        return sum(sr, 0.25, [[render(0.2, sr, (t) => bp.run(n.next()) * env(t, 0.0005, 0.03) * 8), 0.9], [crackles(sr, 0.2, 400, 2400, s + 1, 0.06), 0.4]]);
      }
      case 'leaves': {
        const n = new Noise(s);
        const hp = new Biquad(sr, 'hp');
        hp.set(2500, 0.7);
        return render(0.35, sr, (t) => hp.run(n.next()) * env(t, 0.01, 0.1) * (0.5 + 0.5 * Math.abs(Math.sin(t * 120 + n.next()))));
      }
      case 'snow':
        return sum(sr, 0.3, [[bodyLayer(sr, 0.3, 2000, 600, 0.05, 0.07, s), 0.9], [crackles(sr, 0.25, 700, 3000, s + 1, 0.08), 0.4]]);
      default:
        return sum(sr, 0.3, [[bodyLayer(sr, 0.3, 900, 250, 0.05, 0.08, s), 1.0], [sweepLayer(sr, 0.1, 120, 60, 0.02, 0.04), 0.5], [crackles(sr, 0.2, 250, 900, s + 1, 0.06), 0.35]]);
    }
  };
}
for (const k of ['stone', 'brick', 'concrete', 'wood', 'glass', 'ice', 'crystal', 'metal', 'soft', 'leaves', 'plastic', 'snow'] as const) add(`break_${k}`, 0.8, breakGen(k));

// Bodies, movement ----------------------------------------------------------------------------

add('burst', 0.9, (sr) => {
  const out = sum(sr, 0.9, [[crackles(sr, 0.8, 1200, 2200, seed(), 0.2), 0.8], [sweepLayer(sr, 0.3, 150, 50, 0.03, 0.08), 0.9], [breakGen('crystal')(sr), 0.35, 0.03]]);
  return drive(out, 1.4);
});
add('burst_confetti', 0.85, (sr) => {
  const s = seed();
  return sum(sr, 0.9, [[crackLayer(sr, 0.08, 1200, 0.8, 0.012, s), 1], [crackles(sr, 0.8, 500, 5000, s + 1, 0.3), 0.5, 0.02], [tones(sr, [[84, 0.05, 0.06], [88, 0.1, 0.06], [91, 0.15, 0.12]], 'pulse', 0.1), 0.35]]);
});
add('burst_pixel', 0.8, (sr) => {
  const t = tones(sr, [[84, 0, 0.05], [79, 0.05, 0.05], [76, 0.1, 0.05], [72, 0.15, 0.05], [67, 0.2, 0.05], [60, 0.25, 0.1]], 'square', 0.1);
  // Bit-crush for an 8-bit dissolve.
  for (let i = 0; i < t.length; i++) t[i] = Math.round(t[i] * 6) / 6;
  return sum(sr, 0.5, [[t, 0.7], [crackles(sr, 0.4, 600, 4000, seed(), 0.2), 0.3]]);
});
add('burst_fireworks', 0.85, (sr) => {
  const o = new Osc(sr);
  const whistle = render(0.35, sr, (t) => o.sine(900 + 2200 * (t / 0.35)) * 0.4 * Math.min(1, (0.35 - t) * 20));
  const out = sum(sr, 1.6, [[whistle, 0.6], [crackLayer(sr, 0.1, 900, 0.6, 0.03, seed()), 1, 0.35], [crackles(sr, 1.1, 250, 4200, seed(), 0.5), 0.6, 0.38], [sweepLayer(sr, 0.4, 90, 40, 0.05, 0.1, 0.002), 0.6, 0.35]]);
  return echo(out, sr, [[0.2, 0.25]]);
});
add('burst_bubbles', 0.75, (sr) => {
  const out = new Float32Array(Math.floor(0.8 * sr));
  const n = new Noise(seed());
  for (let k = 0; k < 7; k++) {
    const o = new Osc(sr);
    const f0 = 350 + n.unit() * 500;
    mixInto(out, render(0.12, sr, (t) => o.sine(f0 * (1 + t * 9)) * env(t, 0.004, 0.035)), k * 0.08 + n.unit() * 0.03, sr, 0.7);
  }
  return out;
});
add('burst_gold', 0.8, (sr) => {
  const out = new Float32Array(Math.floor(1.0 * sr));
  const n = new Noise(seed());
  for (let k = 0; k < 12; k++) {
    const f = 2600 + n.unit() * 2200;
    mixInto(out, ringLayer(sr, 0.35, [f, f * 1.51, f * 2.3], 0.09, 0), k * 0.05 + n.unit() * 0.03, sr, 0.35);
  }
  return mixInto(out, tones(sr, [[79, 0, 0.08], [84, 0.08, 0.08], [91, 0.16, 0.25]], 'bell', 0.3), 0, sr, 0.5);
});

add('hurt', 0.8, (sr) => {
  const o = new Osc(sr);
  const buzz = render(0.18, sr, (t) => o.square(110 + 40 * Math.exp(-t / 0.03), 0.3) * env(t, 0.002, 0.05));
  return sum(sr, 0.22, [[lowpass(buzz, sr, 1500), 0.7], [sweepLayer(sr, 0.15, 120, 55, 0.02, 0.05), 0.9], [ringLayer(sr, 0.15, [1600, 2500], 0.04, 0), 0.25]]);
});

function stepGen(kind: 'stone' | 'wood' | 'metal' | 'grass' | 'sand' | 'snow' | 'glass' | 'water', v: number): (sr: number) => Float32Array {
  const s = seed();
  return (sr) => {
    const p = 1 + (v - 0.5) * 0.12;
    switch (kind) {
      case 'stone':
        return sum(sr, 0.12, [[crackLayer(sr, 0.06, 2600 * p, 1.2, 0.005, s), 0.7], [sweepLayer(sr, 0.06, 120 * p, 70, 0.01, 0.02), 0.6], [bodyLayer(sr, 0.08, 3000, 1500, 0.02, 0.015, s + 1), 0.35]]);
      case 'wood': {
        const bp = new Biquad(sr, 'bp');
        bp.set(330 * p, 6);
        const n = new Noise(s);
        return sum(sr, 0.14, [[render(0.12, sr, (t) => bp.run(n.next()) * env(t, 0.0005, 0.03) * 6), 0.8], [crackLayer(sr, 0.04, 1800, 1, 0.003, s + 1), 0.4]]);
      }
      case 'metal':
        return sum(sr, 0.2, [[ringLayer(sr, 0.18, [720 * p, 1680 * p, 2710 * p], 0.05, 0), 0.4], [crackLayer(sr, 0.05, 2200, 1, 0.004, s), 0.6]]);
      case 'grass':
        return sum(sr, 0.14, [[bodyLayer(sr, 0.12, 2500 * p, 900, 0.02, 0.03, s), 0.8], [crackles(sr, 0.1, 400, 3200, s + 1, 0.04), 0.35]]);
      case 'sand':
        return highpass(bodyLayer(sr, 0.16, 5000 * p, 2500, 0.03, 0.045, s, 0.01), sr, 1200);
      case 'snow':
        return sum(sr, 0.18, [[bodyLayer(sr, 0.16, 1800 * p, 700, 0.03, 0.05, s, 0.012), 0.8], [crackles(sr, 0.14, 900, 2600, s + 1, 0.06), 0.5]]);
      case 'glass':
        return sum(sr, 0.16, [[crackLayer(sr, 0.05, 3400 * p, 1.4, 0.004, s), 0.6], [ringLayer(sr, 0.14, [3100 * p, 4700 * p], 0.03, 0), 0.25], [sweepLayer(sr, 0.05, 140, 80, 0.01, 0.015), 0.4]]);
      case 'water':
        // Wading: a swish of water, droplets and a small bubble bloop.
        return sum(sr, 0.24, [
          [bodyLayer(sr, 0.22, 2600 * p, 700, 0.05, 0.06, s, 0.008), 0.9],
          [crackles(sr, 0.2, 900, 3800 * p, s + 1, 0.08), 0.5],
          [sweepLayer(sr, 0.1, 380 * p, 900 * p, 0.03, 0.03, 0.002), 0.25],
        ]);
    }
  };
}
for (const k of ['stone', 'wood', 'metal', 'grass', 'sand', 'snow', 'glass', 'water'] as const) {
  add(`step_${k}_a`, 0.55, stepGen(k, 0.2));
  add(`step_${k}_b`, 0.55, stepGen(k, 0.8));
}
add('step', 0.55, stepGen('stone', 0.5));
add('land', 0.7, (sr) => sum(sr, 0.22, [[sweepLayer(sr, 0.15, 110, 50, 0.02, 0.05), 1.0], [bodyLayer(sr, 0.2, 1500, 300, 0.02, 0.04, seed()), 0.8], [crackles(sr, 0.15, 400, 2000, seed(), 0.05), 0.3]]));
add('jump', 0.45, (sr) => sum(sr, 0.16, [[whoosh(sr, 0.14, 300, 900, 500, 1, seed()), 0.7], [crackLayer(sr, 0.04, 1500, 1, 0.004, seed()), 0.3]]));

// Weapon handling foley -------------------------------------------------------------------------

const click = (sr: number, f: number, dec = 0.006, lvl = 1): Float32Array => {
  const out = crackLayer(sr, 0.06, f, 1.6, dec, seed());
  return mixInto(out, ringLayer(sr, 0.06, [f * 1.3, f * 2.1], 0.01), 0, sr, 0.3 * lvl);
};
const scrape = (sr: number, dur: number, f: number, lvl = 1): Float32Array => {
  const n = new Noise(seed());
  const bp = new Biquad(sr, 'bp');
  return render(dur, sr, (t) => bp.run(n.next(), f * (0.9 + 0.2 * (t / dur)), 2) * Math.sin(Math.PI * (t / dur)) * 3 * lvl);
};
add('reload', 0.6, (sr) => sum(sr, 0.5, [[click(sr, 2400), 0.8, 0.02], [click(sr, 1800), 0.8, 0.3], [click(sr, 2800), 0.6, 0.34]]));
add('empty', 0.55, (sr) => click(sr, 3200, 0.004));
add('mag_out', 0.6, (sr) => sum(sr, 0.25, [[click(sr, 2600), 0.8], [scrape(sr, 0.12, 1500), 0.4, 0.02], [click(sr, 1400, 0.01), 0.4, 0.12]]));
add('mag_in', 0.7, (sr) => sum(sr, 0.22, [[scrape(sr, 0.06, 1600), 0.4], [click(sr, 1900, 0.01), 1.0, 0.06], [sweepLayer(sr, 0.06, 180, 110, 0.01, 0.02), 0.5, 0.06], [click(sr, 2700, 0.006), 0.6, 0.12]]));
add('rack', 0.7, (sr) => sum(sr, 0.3, [[click(sr, 2300), 0.8], [scrape(sr, 0.08, 2000), 0.5, 0.02], [click(sr, 1700, 0.012), 1.0, 0.12]]));
add('cell_out', 0.55, (sr) => {
  const o = new Osc(sr);
  return sum(sr, 0.3, [[click(sr, 2800), 0.7], [render(0.2, sr, (t) => o.square(900 - 2500 * t, 0.3) * env(t, 0.004, 0.06)), 0.25, 0.03], [highpass(bodyLayer(sr, 0.2, 7000, 4000, 0.05, 0.05, seed()), sr, 2500), 0.4, 0.02]]);
});
add('cell_in', 0.6, (sr) => {
  const o = new Osc(sr);
  return sum(sr, 0.35, [[click(sr, 2000, 0.01), 1.0], [render(0.25, sr, (t) => o.square(400 + 2400 * t, 0.3) * env(t, 0.01, 0.1)), 0.25, 0.06]]);
});
add('cell_charge', 0.5, (sr) => {
  const o = new Osc(sr);
  const o2 = new Osc(sr);
  return render(0.45, sr, (t) => (o.sine(500 + 1800 * t) + o2.sine(1000 + 3600 * t) * 0.3) * ahr(t, 0.05, 0.25, 0.15) * 0.6);
});
add('belt_out', 0.65, (sr) => sum(sr, 0.4, [[click(sr, 1500, 0.015), 1.0], [crackles(sr, 0.3, 300, 3000, seed(), 0.12), 0.5, 0.04], [sweepLayer(sr, 0.1, 140, 80, 0.02, 0.03), 0.5]]));
add('belt_in', 0.7, (sr) => sum(sr, 0.4, [[crackles(sr, 0.2, 400, 3200, seed(), 0.08), 0.4], [click(sr, 1300, 0.02), 1.0, 0.16], [sweepLayer(sr, 0.1, 150, 70, 0.02, 0.04), 0.7, 0.16]]));
add('shell_in', 0.55, (sr) => sum(sr, 0.16, [[scrape(sr, 0.05, 1300, 0.7), 0.6], [click(sr, 1600, 0.01), 0.8, 0.045]]));
add('shells_eject', 0.5, (sr) => {
  const out = new Float32Array(Math.floor(0.6 * sr));
  const n = new Noise(seed());
  for (let k = 0; k < 6; k++) {
    const f = 3400 + n.unit() * 1600;
    mixInto(out, ringLayer(sr, 0.12, [f, f * 1.4], 0.03, 0), 0.05 + k * 0.06 + n.unit() * 0.03, sr, 0.4);
  }
  return out;
});
add('pump', 0.75, (sr) => sum(sr, 0.35, [[click(sr, 1500, 0.012), 1.0], [scrape(sr, 0.08, 900), 0.6, 0.02], [click(sr, 1200, 0.015), 1.0, 0.16], [sweepLayer(sr, 0.06, 150, 90, 0.01, 0.02), 0.5, 0.16]]));
add('break_open', 0.65, (sr) => sum(sr, 0.3, [[click(sr, 1100, 0.02), 1.0], [ringLayer(sr, 0.2, [900, 1500], 0.05, 0.01), 0.3]]));
add('break_close', 0.75, (sr) => sum(sr, 0.25, [[click(sr, 1700, 0.012), 1.0], [sweepLayer(sr, 0.08, 170, 100, 0.01, 0.025), 0.6]]));
add('bolt_up', 0.55, (sr) => click(sr, 2100, 0.01));
add('bolt_back', 0.6, (sr) => sum(sr, 0.2, [[scrape(sr, 0.12, 1800), 0.7], [click(sr, 1500, 0.01), 0.6, 0.12]]));
add('bolt_fwd', 0.65, (sr) => sum(sr, 0.25, [[scrape(sr, 0.1, 2000), 0.6], [click(sr, 1300, 0.012), 1.0, 0.1], [click(sr, 2400, 0.006), 0.6, 0.16]]));
add('bolt_cycle', 0.65, (sr) => sum(sr, 0.7, [[click(sr, 2100, 0.01), 0.7], [scrape(sr, 0.1, 1800), 0.6, 0.1], [scrape(sr, 0.1, 2000), 0.6, 0.3], [click(sr, 1300, 0.012), 1.0, 0.4], [click(sr, 2400, 0.006), 0.6, 0.5]]));
add('cyl_open', 0.6, (sr) => sum(sr, 0.3, [[click(sr, 2200, 0.01), 0.8], [crackles(sr, 0.15, 500, 3000, seed(), 0.08), 0.4, 0.03]]));
add('cyl_close', 0.7, (sr) => sum(sr, 0.5, [[click(sr, 1600, 0.012), 1.0], [crackles(sr, 0.35, 700, 2600, seed(), 0.15), 0.45, 0.04]]));
add('launcher_load', 0.75, (sr) => sum(sr, 0.5, [[scrape(sr, 0.25, 700), 0.6], [click(sr, 900, 0.03), 1.0, 0.26], [sweepLayer(sr, 0.12, 120, 60, 0.02, 0.05), 0.7, 0.26]]));
add('crossbow_draw', 0.55, (sr) => {
  const n = new Noise(seed());
  const bp = new Biquad(sr, 'bp');
  const creak = render(0.5, sr, (t) => bp.run(n.next(), 400 + 900 * (t / 0.5), 8) * (0.6 + 0.4 * Math.sin(t * 160)) * Math.sin(Math.PI * (t / 0.5)) * 6);
  return sum(sr, 0.55, [[creak, 0.8], [click(sr, 1400, 0.015), 0.8, 0.48]]);
});
add('crossbow_load', 0.6, (sr) => sum(sr, 0.25, [[scrape(sr, 0.1, 1500), 0.5], [click(sr, 1800, 0.01), 0.9, 0.1]]));
add('equip_rifle', 0.55, (sr) => sum(sr, 0.3, [[whoosh(sr, 0.14, 300, 900, 500, 1, seed()), 0.4], [click(sr, 1500, 0.015), 0.9, 0.12], [click(sr, 2600, 0.008), 0.5, 0.18]]));
add('equip_pistol', 0.5, (sr) => sum(sr, 0.25, [[scrape(sr, 0.1, 1600, 0.8), 0.5], [click(sr, 2400, 0.008), 0.9, 0.1]]));
add('equip_heavy', 0.6, (sr) => sum(sr, 0.45, [[sweepLayer(sr, 0.15, 120, 60, 0.02, 0.05), 0.8, 0.1], [click(sr, 900, 0.03), 1.0, 0.1], [crackles(sr, 0.25, 300, 2500, seed(), 0.1), 0.35, 0.12]]));
add('equip_melee', 0.5, (sr) => sum(sr, 0.3, [[whoosh(sr, 0.2, 250, 800, 400, 1, seed()), 0.6], [sweepLayer(sr, 0.08, 140, 80, 0.01, 0.03), 0.6, 0.18]]));
add('equip_blade', 0.55, (sr) => sum(sr, 0.5, [[scrape(sr, 0.25, 3200, 0.8), 0.6], [ringLayer(sr, 0.4, [3100, 4700, 6900], 0.12, 0.22), 0.4]]));

// Combat feedback ------------------------------------------------------------------------------

add('hit', 0.6, (sr) => sum(sr, 0.08, [[ringLayer(sr, 0.07, [1900, 2850], 0.012), 0.8], [crackLayer(sr, 0.03, 3500, 1.5, 0.002, seed()), 0.4]]));
add('hit_armor', 0.6, (sr) => sum(sr, 0.14, [[ringLayer(sr, 0.12, [2400, 3700, 5300], 0.025), 0.7], [crackLayer(sr, 0.04, 2800, 1.5, 0.003, seed()), 0.4]]));
add('headshot', 0.75, (sr) => sum(sr, 0.45, [[tones(sr, [[91, 0, 0.1], [98, 0.045, 0.2]], 'bell', 0.25), 0.8], [crackLayer(sr, 0.04, 4000, 1.5, 0.003, seed()), 0.4]]));
add('kill', 0.75, (sr) => sum(sr, 0.6, [[tones(sr, [[81, 0, 0.07], [88, 0.07, 0.25]], 'bell', 0.3), 0.8], [sweepLayer(sr, 0.12, 200, 90, 0.02, 0.04), 0.5]]));
add('streak', 0.75, (sr) => tones(sr, [[76, 0, 0.08], [81, 0.08, 0.08], [84, 0.16, 0.08], [88, 0.24, 0.3]], 'pulse', 0.25));

// UI ------------------------------------------------------------------------------------------

add('ui_click', 0.45, (sr) => sum(sr, 0.06, [[ringLayer(sr, 0.05, [1250, 2500], 0.01), 0.8], [crackLayer(sr, 0.02, 3000, 2, 0.001, seed()), 0.2]]));
add('ui_hover', 0.25, (sr) => ringLayer(sr, 0.04, [2100], 0.008));
add('ui_tab', 0.4, (sr) => tones(sr, [[84, 0, 0.03]], 'tri', 0.04));
add('ui_toggle', 0.4, (sr) => tones(sr, [[79, 0, 0.025], [86, 0.04, 0.03]], 'tri', 0.04));
add('ui_back', 0.4, (sr) => tones(sr, [[81, 0, 0.04], [74, 0.05, 0.05]], 'tri', 0.05));
add('ui_open', 0.45, (sr) => sum(sr, 0.35, [[whoosh(sr, 0.18, 400, 2000, 1500, 1, seed()), 0.35], [tones(sr, [[84, 0.06, 0.08], [91, 0.12, 0.15]], 'bell', 0.2), 0.6]]));
add('ui_close', 0.4, (sr) => sum(sr, 0.25, [[whoosh(sr, 0.18, 1500, 800, 300, 1, seed()), 0.35], [tones(sr, [[86, 0, 0.06], [79, 0.06, 0.1]], 'tri', 0.1), 0.5]]));
add('ui_error', 0.45, (sr) => tones(sr, [[50, 0, 0.1], [47, 0.12, 0.16]], 'square', 0.08));
add('ui_buy', 0.6, (sr) => sum(sr, 0.7, [[tones(sr, [[79, 0, 0.08], [83, 0.08, 0.08], [86, 0.16, 0.08], [91, 0.24, 0.3]], 'bell', 0.3), 0.8], [SOUNDS.coins.gen(sr), 0.4, 0.2]]));
add('ui_equip', 0.5, (sr) => sum(sr, 0.4, [[click(sr, 1800, 0.012), 0.8], [tones(sr, [[88, 0.05, 0.08], [95, 0.1, 0.2]], 'bell', 0.2), 0.5]]));
add('coins', 0.55, (sr) => {
  const out = new Float32Array(Math.floor(0.6 * sr));
  const n = new Noise(seed());
  for (let k = 0; k < 7; k++) {
    const f = 2800 + n.unit() * 1500;
    mixInto(out, ringLayer(sr, 0.2, [f, f * 1.5, f * 2.2], 0.06, 0), k * 0.045 + n.unit() * 0.02, sr, 0.35);
  }
  return out;
});
add('gems', 0.55, (sr) => tones(sr, [[88, 0, 0.1], [95, 0.06, 0.12], [100, 0.12, 0.3], [103, 0.2, 0.4]], 'bell', 0.4));
add('reward', 0.6, (sr) => sum(sr, 1.0, [[tones(sr, [[72, 0, 0.1], [76, 0.1, 0.1], [79, 0.2, 0.1], [84, 0.3, 0.45]], 'pulse', 0.3), 0.6], [tones(sr, [[96, 0.3, 0.3], [100, 0.4, 0.3]], 'bell', 0.3), 0.4]]));
add('unlock', 0.65, (sr) => sum(sr, 1.2, [[tones(sr, [[67, 0, 0.1], [72, 0.1, 0.1], [76, 0.2, 0.1], [79, 0.3, 0.1], [84, 0.4, 0.5]], 'square', 0.35), 0.5], [tones(sr, [[84, 0.4, 0.5], [88, 0.4, 0.5], [91, 0.4, 0.5]], 'bell', 0.4), 0.4]]));
add('levelup', 0.65, (sr) => {
  const out = sum(sr, 1.6, [[tones(sr, [[72, 0, 0.12], [76, 0.12, 0.12], [79, 0.24, 0.12], [84, 0.36, 0.12], [88, 0.48, 0.6]], 'pulse', 0.4), 0.55], [tones(sr, [[60, 0.48, 0.7], [64, 0.48, 0.7], [67, 0.48, 0.7]], 'tri', 0.4), 0.5], [tones(sr, [[96, 0.5, 0.4], [100, 0.62, 0.4], [103, 0.74, 0.5]], 'bell', 0.4), 0.35]]);
  return reverb(out, sr, 0.25, 1.2);
});
add('match_found', 0.65, (sr) => reverb(tones(sr, [[76, 0, 0.1], [83, 0.1, 0.1], [88, 0.2, 0.4]], 'bell', 0.5), sr, 0.3));
add('countdown', 0.55, (sr) => tones(sr, [[81, 0, 0.12]], 'square', 0.08));
add('countdown_go', 0.6, (sr) => tones(sr, [[93, 0, 0.35]], 'square', 0.15));
add('chat', 0.35, (sr) => tones(sr, [[86, 0, 0.04], [91, 0.05, 0.06]], 'sine', 0.06));
add('notify', 0.45, (sr) => tones(sr, [[84, 0, 0.08], [88, 0.1, 0.16]], 'bell', 0.25));
add('xp_tick', 0.25, (sr) => tones(sr, [[96, 0, 0.02]], 'sine', 0.03));

// Pickups & gadgets ------------------------------------------------------------------------------

add('pickup', 0.55, (sr) => tones(sr, [[79, 0, 0.06], [86, 0.06, 0.12]], 'pulse', 0.12));
add('pickup_health', 0.55, (sr) => tones(sr, [[72, 0, 0.07], [76, 0.07, 0.07], [79, 0.14, 0.07], [84, 0.21, 0.2]], 'tri', 0.2));
add('pickup_armor', 0.6, (sr) => sum(sr, 0.5, [[click(sr, 1300, 0.02), 0.9], [ringLayer(sr, 0.4, [1560, 2340, 3900], 0.12, 0.02), 0.4], [tones(sr, [[88, 0.08, 0.2]], 'bell', 0.2), 0.3]]));
add('pickup_ammo', 0.55, (sr) => sum(sr, 0.35, [[click(sr, 2200, 0.01), 0.8], [click(sr, 1700, 0.01), 0.8, 0.06], [click(sr, 2600, 0.008), 0.6, 0.12], [tones(sr, [[84, 0.12, 0.08]], 'tri', 0.1), 0.35]]));
add('pickup_weapon', 0.6, (sr) => sum(sr, 0.5, [[click(sr, 1200, 0.02), 1.0], [tones(sr, [[76, 0.05, 0.08], [83, 0.12, 0.2]], 'bell', 0.25), 0.5]]));
add('gadget', 0.55, (sr) => sum(sr, 0.3, [[whoosh(sr, 0.22, 300, 1200, 500, 1, seed()), 0.8], [click(sr, 1600, 0.01), 0.5]]));
add('heal', 0.55, (sr) => reverb(tones(sr, [[72, 0, 0.12], [79, 0.08, 0.12], [84, 0.16, 0.12], [88, 0.24, 0.3]], 'bell', 0.35), sr, 0.25));
add('stim', 0.6, (sr) => {
  const o = new Osc(sr);
  return sum(sr, 0.5, [[render(0.4, sr, (t) => o.saw(200 + 1600 * t) * env(t, 0.01, 0.15)), 0.4], [crackLayer(sr, 0.05, 3000, 1, 0.005, seed()), 0.6], [tones(sr, [[88, 0.15, 0.15]], 'square', 0.1), 0.3]]);
});
add('wall_deploy', 0.8, (sr) => sum(sr, 0.7, [[sweepLayer(sr, 0.3, 90, 40, 0.05, 0.12), 1.0], [click(sr, 700, 0.04), 1.0], [scrape(sr, 0.35, 400), 0.6, 0.05], [ringLayer(sr, 0.5, [440, 1030, 1720], 0.18, 0.02), 0.3]]));
add('respawn', 0.55, (sr) => reverb(tones(sr, [[72, 0, 0.1], [76, 0.1, 0.1], [79, 0.2, 0.1], [84, 0.3, 0.3]], 'pulse', 0.25), sr, 0.2));

// Objective jingles (no voice) -------------------------------------------------------------------

const jingle = (list: [number, number, number, number?][], timbre: 'square' | 'pulse' | 'tri' | 'bell' | 'saw', rev = 0.25) => (sr: number) => reverb(tones(sr, list, timbre, 0.35), sr, rev);
add('capture', 0.65, jingle([[72, 0, 0.1], [76, 0.1, 0.1], [79, 0.2, 0.1], [84, 0.3, 0.35]], 'pulse'));
add('capture_lost', 0.6, jingle([[76, 0, 0.12], [72, 0.12, 0.12], [69, 0.24, 0.12], [63, 0.36, 0.4]], 'square'));
add('capture_tick', 0.3, (sr) => tones(sr, [[88, 0, 0.02]], 'sine', 0.02));
add('flag_taken', 0.65, jingle([[84, 0, 0.1], [79, 0.12, 0.1], [84, 0.24, 0.1], [79, 0.36, 0.1]], 'square', 0.15));
add('flag_captured', 0.7, jingle([[72, 0, 0.1], [76, 0.1, 0.1], [79, 0.2, 0.1], [84, 0.3, 0.12], [79, 0.42, 0.1], [84, 0.52, 0.45]], 'pulse'));
add('flag_returned', 0.6, jingle([[79, 0, 0.1], [83, 0.1, 0.1], [86, 0.2, 0.3]], 'bell'));
add('flag_dropped', 0.55, jingle([[76, 0, 0.1], [72, 0.1, 0.1], [67, 0.2, 0.3]], 'tri'));
add('objective', 0.55, jingle([[84, 0, 0.08], [91, 0.08, 0.25]], 'bell'));
add('round_start', 0.7, (sr) => sum(sr, 1.4, [[jingle([[60, 0, 0.5], [67, 0, 0.5], [72, 0, 0.5], [76, 0.5, 0.6], [79, 0.5, 0.6], [84, 0.5, 0.6]], 'saw', 0.3)(sr), 0.5], [sweepLayer(sr, 0.4, 120, 45, 0.04, 0.12), 0.8, 0.5]]));
add('round_win', 0.7, jingle([[72, 0, 0.12], [76, 0.12, 0.12], [79, 0.24, 0.12], [84, 0.36, 0.2], [88, 0.56, 0.6], [91, 0.56, 0.6]], 'pulse'));
add('round_lose', 0.6, jingle([[72, 0, 0.2], [70, 0.2, 0.2], [67, 0.4, 0.2], [63, 0.6, 0.7]], 'tri'));
add('core_hit', 0.7, (sr) => sum(sr, 0.9, [[breakGen('crystal')(sr), 0.7], [sweepLayer(sr, 0.3, 90, 45, 0.03, 0.1), 0.8], [tones(sr, [[64, 0, 0.3]], 'bell', 0.4), 0.3]]));
add('core_destroyed', 0.9, (sr) => sum(sr, 3.0, [[boomGen(1.1, false)(sr), 1.0], [breakGen('crystal')(sr), 0.8, 0.05], [jingle([[64, 0.3, 0.3], [59, 0.6, 0.3], [52, 0.9, 1.0]], 'saw')(sr), 0.35]]));
add('zone_warning', 0.6, jingle([[81, 0, 0.14], [77, 0.2, 0.14], [81, 0.4, 0.14], [77, 0.6, 0.14]], 'square', 0.1));
add('wave_start', 0.75, (sr) => sum(sr, 1.8, [[jingle([[45, 0, 1.0], [52, 0, 1.0], [57, 0.3, 1.2]], 'saw', 0.4)(sr), 0.6], [sweepLayer(sr, 0.6, 70, 32, 0.1, 0.25), 1.0], [sweepLayer(sr, 0.6, 70, 32, 0.1, 0.25), 0.8, 0.6]]));
add('wave_clear', 0.65, jingle([[76, 0, 0.1], [79, 0.1, 0.1], [84, 0.2, 0.1], [88, 0.3, 0.1], [91, 0.4, 0.45]], 'bell'));

// Loops ----------------------------------------------------------------------------------------

add('loop_laser', 0.45, (sr) => {
  const o = new Osc(sr);
  const o2 = new Osc(sr);
  return render(1, sr, (t) => o.sine(440 + 12 * Math.sin(TAU * 6 * t)) * 0.7 + o2.saw(220) * 0.2);
}, true);
add('loop_mining', 0.5, (sr) => {
  const s = seed();
  const n = new Noise(s);
  const bp = new Biquad(sr, 'bp');
  const o = new Osc(sr);
  const o2 = new Osc(sr);
  return render(1.5, sr, (t) => o.sine(880) * 0.25 + o2.sine(1320) * 0.12 + bp.run(n.next(), 1600, 1.5) * (0.6 + 0.4 * Math.sin(TAU * 20 * t)) * 1.2);
}, true);
add('loop_flame', 0.6, (sr) => {
  const s = seed();
  const n = new Noise(s);
  const lp = new LP1(sr);
  const lp2 = new LP1(sr);
  const cr = crackles(sr, 1.5, 60, 2500, s + 1, 1e9);
  const out = render(1.5, sr, (t, i) => lp2.run(lp.run(n.next(), 900), 700) * 3 * (0.75 + 0.25 * Math.sin(TAU * 11 * t + Math.sin(TAU * 3 * t) * 2)) + cr[i] * 0.3);
  return out;
}, true);
add('loop_tesla', 0.55, (sr) => {
  const s = seed();
  const o = new Osc(sr);
  const cr = crackles(sr, 1.5, 70, 3500, s, 1e9);
  const hum = lowpass(render(1.5, sr, () => o.saw(60)), sr, 700);
  return render(1.5, sr, (_t, i) => hum[i] * 0.5 + cr[i] * 0.9);
}, true);
add('loop_heartbeat', 0.7, (sr) => {
  const beat = (t: number): number => {
    const a = t < 0 ? 0 : Math.sin(TAU * (48 + 30 * Math.exp(-t / 0.03)) * t) * env(t, 0.004, 0.08);
    return a;
  };
  const out = render(1.0, sr, (t) => beat(t) + beat(t - 0.2) * 0.7);
  return lowpass(out, sr, 400);
}, true);
add('loop_minigun_spin', 0.45, (sr) => {
  const o = new Osc(sr);
  const s = seed();
  const n = new Noise(s);
  const bp = new Biquad(sr, 'bp');
  const o2 = new Osc(sr);
  const lp = new LP1(sr);
  return render(1, sr, (t) => lp.run(o.saw(46), 1000) * 0.2 + o2.saw(92) * 0.25 + bp.run(n.next(), 2300, 3) * (0.5 + 0.5 * Math.sin(TAU * 23 * t)) * 0.8);
}, true);
add('loop_charge', 0.45, (sr) => {
  const o = new Osc(sr);
  const o2 = new Osc(sr);
  return render(1, sr, (t) => o.sine(1200 + 40 * Math.sin(TAU * 8 * t)) * 0.5 + o2.square(600, 0.5) * 0.08);
}, true);
add('loop_capture', 0.35, (sr) => {
  const o = new Osc(sr);
  const o2 = new Osc(sr);
  return render(1, sr, (t) => (o.sine(220) + o2.sine(330) * 0.6) * (0.6 + 0.4 * Math.sin(TAU * 4 * t)));
}, true);
add('loop_zone', 0.5, (sr) => {
  const s = seed();
  const cr = crackles(sr, 2, 20, 900, s, 1e9);
  const body = bodyLayer(sr, 2, 260, 180, 1, 1e9, s + 1);
  return render(2, sr, (_t, i) => body[i] * 1.2 + cr[i] * 0.5);
}, true);
add('loop_wind', 0.4, (sr) => {
  const n = new Noise(seed());
  const bp = new Biquad(sr, 'bp');
  return render(3, sr, (t) => bp.run(n.next(), 500 + 300 * Math.sin(TAU * 0.33 * t) + 150 * Math.sin(TAU * 1.0 * t), 1.2) * 2);
}, true);

// Pets ------------------------------------------------------------------------------------------

/** Attacker bite: a quick snap of teeth with a little gnash. */
add('pet_bite', 0.8, (sr) =>
  sum(sr, 0.34, [
    [whoosh(sr, 0.1, 500, 1800, 900, 1, seed()), 0.35],
    [click(sr, 2300, 0.005), 1.0, 0.06],
    [sweepLayer(sr, 0.12, 240, 110, 0.02, 0.035), 0.9, 0.06],
    [crackles(sr, 0.12, 700, 2600, seed(), 0.04), 0.4, 0.07],
    [click(sr, 1600, 0.008), 0.7, 0.16],
  ]),
);

/** Scout ping: a soft owl "hoo-hoo" with a sonar bell under it. */
add('pet_hoot', 0.7, (sr) => {
  const hoo = (f0: number, dur: number): Float32Array => {
    const o = new Osc(sr);
    const o2 = new Osc(sr);
    const n = new Noise(seed());
    const lp = new LP1(sr);
    return render(dur + 0.08, sr, (t) => {
      const f = f0 * (1 + 0.05 * Math.sin((Math.PI * t) / dur)) * (1 + 0.012 * Math.sin(TAU * 7 * t));
      const breath = lp.run(n.next(), 1100) * 0.3;
      return (o.sine(f) + o2.sine(f * 2) * 0.16 + breath) * ahr(t, 0.03, Math.max(0, dur - 0.06), 0.08);
    });
  };
  const out = sum(sr, 1.1, [
    [hoo(392, 0.15), 0.85, 0],
    [hoo(440, 0.26), 1.0, 0.2],
    [ringLayer(sr, 0.8, [1760, 2640, 3520], 0.16, 0, [1, 0.4, 0.15]), 0.24, 0.04],
  ]);
  return echo(out, sr, [
    [0.16, 0.25],
    [0.32, 0.12],
  ]);
});

/** Medic heal pulse: a gentle rising chime with a sparkle. */
add('pet_heal', 0.5, (sr) =>
  sum(sr, 0.9, [
    [reverb(tones(sr, [[88, 0, 0.08], [91, 0.06, 0.08], [96, 0.12, 0.22]], 'bell', 0.3), sr, 0.3), 0.9],
    [highpass(crackles(sr, 0.5, 260, 6200, seed(), 0.25), sr, 3000), 0.25, 0.05],
  ]),
);

/** Digger chewing a hole: a whirring grind with grit. */
add('pet_drill', 0.75, (sr) => {
  const o = new Osc(sr);
  const o2 = new Osc(sr);
  const n = new Noise(seed());
  const bp = new Biquad(sr, 'bp');
  const whir = render(0.78, sr, (t) => {
    const f = 150 + 190 * Math.min(1, t / 0.12);
    const v = o.saw(f) * 0.5 + o2.square(f * 2.01, 0.4) * 0.18 + bp.run(n.next(), 2300 + 700 * Math.sin(TAU * 30 * t), 2.5) * 1.1;
    return v * ahr(t, 0.02, 0.56, 0.18) * (0.8 + 0.2 * Math.sin(TAU * 38 * t));
  });
  return sum(sr, 0.82, [
    [lowpass(whir, sr, 5000), 1],
    [crackles(sr, 0.7, 520, 2200, seed(), 0.4), 0.55, 0.05],
  ]);
});

/** Guard soaking a hit: a bright shield ring over a soft thump. */
add('pet_absorb', 0.7, (sr) => {
  const o = new Osc(sr);
  const zap = render(0.16, sr, (t) => o.sine(600 + 5200 * t) * env(t, 0.002, 0.05));
  return sum(sr, 0.55, [
    [ringLayer(sr, 0.5, [880, 1320, 2250, 3100], 0.12, 0, [1, 0.6, 0.35, 0.2]), 0.55],
    [sweepLayer(sr, 0.2, 180, 70, 0.03, 0.06), 0.8],
    [zap, 0.35, 0.01],
  ]);
});

/** Pet shot apart: a cartoon pop, a falling blip and scattering voxels. */
add('pet_pop', 0.8, (sr) => {
  const o = new Osc(sr);
  const blip = render(0.2, sr, (t) => o.square(900 * Math.exp(-t / 0.08) + 180, 0.35) * env(t, 0.002, 0.07));
  return sum(sr, 0.75, [
    [crackLayer(sr, 0.08, 1400, 0.8, 0.012, seed()), 1.0],
    [lowpass(blip, sr, 2600), 0.45, 0.02],
    [crackles(sr, 0.6, 900, 2800, seed(), 0.18), 0.6, 0.03],
    [sweepLayer(sr, 0.2, 170, 60, 0.03, 0.06), 0.6],
  ]);
});

/** Pet re-forming: voxels whoosh together into an ascending sparkle. */
add('pet_reform', 0.6, (sr) =>
  sum(sr, 0.95, [
    [whoosh(sr, 0.3, 300, 2200, 900, 1, seed()), 0.3],
    [reverb(tones(sr, [[79, 0.08, 0.06], [84, 0.14, 0.06], [88, 0.2, 0.06], [91, 0.26, 0.06], [96, 0.32, 0.3]], 'bell', 0.3), sr, 0.25), 0.75],
  ]),
);

/** A pet taking a hit: a small cartoon "bonk". */
add('pet_hurt', 0.55, (sr) => {
  const o = new Osc(sr);
  const b = render(0.14, sr, (t) => o.square(1150 - 2600 * t, 0.3) * env(t, 0.002, 0.04));
  return sum(sr, 0.18, [
    [lowpass(b, sr, 3000), 0.6],
    [click(sr, 1800, 0.006), 0.7],
  ]);
});

/** You were spotted by an enemy scout. */
add('pet_spotted', 0.5, (sr) =>
  sum(sr, 0.45, [
    [tones(sr, [[84, 0, 0.07], [79, 0.09, 0.14]], 'tri', 0.1), 0.8],
    [click(sr, 2600, 0.004), 0.3],
  ]),
);

/** Renders one sound (normalized, faded or loop-ready). */
export function generateSound(name: string, sr: number): Float32Array | null {
  const d = SOUNDS[name];
  if (!d) return null;
  let buf = d.gen(sr);
  if (d.loop) {
    buf = loopify(buf, sr);
    return finish(buf, sr, d.peak, 0, 0);
  }
  return finish(buf, sr, d.peak, 0.0008, Math.min(0.03, buf.length / sr / 5));
}
